import { cn } from '@/lib/utils';
import { ElementType } from 'react';

interface AppBadgeProps {
  children?: React.ReactNode;
  className?: string;
  size?: 'small' | 'medium';
  variant?: 'label' | 'solid' | 'outline';
  color?: 'primary' | 'success' | 'error' | 'warning' | 'info' | string;
  icon?: ElementType;
  circle?: boolean;
  iconOnly?: boolean;
}

const colorMap: Record<string, { label: string; solid: string; outline: string }> = {
  primary: {
    label: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200',
    solid: 'bg-primary text-white',
    outline: 'border border-primary text-primary',
  },
  success: {
    label: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-200',
    solid: 'bg-success text-white',
    outline: 'border border-success text-success',
  },
  error: {
    label: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-200',
    solid: 'bg-danger text-white',
    outline: 'border border-danger text-danger',
  },
  warning: {
    label: 'bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-200',
    solid: 'bg-warning text-white',
    outline: 'border border-warning text-warning',
  },
  info: {
    label: 'bg-info-50 text-info-700 ring-1 ring-inset ring-info-200',
    solid: 'bg-info text-white',
    outline: 'border border-info text-info',
  },
};

const sizeMap = {
  small: 'text-xs px-2 py-0.5',
  medium: 'text-sm px-2.5 py-1',
};

export default function AppBadge({
  children,
  className,
  size = 'medium',
  variant = 'label',
  color = 'primary',
  icon: Icon,
  circle = false,
  iconOnly = false,
}: AppBadgeProps) {
  const colorStyles = colorMap[color] || colorMap.primary;
  const variantStyle = colorStyles[variant] || colorStyles.label;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium capitalize',
        circle ? 'rounded-full' : 'rounded-md',
        sizeMap[size],
        variantStyle,
        iconOnly && 'p-1',
        className
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {!iconOnly && children}
    </span>
  );
}
